import { ApolloError } from "@apollo/client";
import { Centered, Container, SearchableCard } from "@snapchat/snap-design-system";
import { type FC } from "react";
import { Visible } from "../../common/components";
import { searchableCardHeaderStyle, searchItemsContainer } from "../../common/styles";
import { useModal } from "../../core/hooks";
import { DashboardSpinner } from "../../dashboard/components/DashboardSpinner";
import { NoResults } from "../../groups/components/NoResults";
import { AddLensSourceContainer } from "../container/AddLensSourceContainer";
import { UpdateLensSourceContainer } from "../container/UpdateLensSourceContainer";
import { LensSourcesDomainModel } from "../domain/LensSourcesDomainModel";
import { type ViewSourceModel } from "../models";
import { DeleteLensSourceModal } from "./DeleteLensSourceModal";
import { SourcesGroup } from "./SourcesGroup";
import { ApiErrorNotice } from "../../common/components/ApiErrorNotice";
import { LenssourceModelLensSourceType as LensSourceType } from "../../../gql-generated/types";

const noSearchResultsMessage = "Your search criteria did not match any lens source.";
const noLensSourcesAvailableMessage = "There are no lens source available.";

interface EnabledLensSourcesProps {
    loading: boolean;
    error?: ApolloError;
    sourcesDomainModel: LensSourcesDomainModel;
    setSearchValue: (searchValue: string) => void;
}

export const EnabledLensSources: FC<EnabledLensSourcesProps> = ({
    setSearchValue,
    loading,
    error,
    sourcesDomainModel,
}) => {
    const { filterService, filteredAllSourcesGrouped, isAnySourceEnabled, hasFilteredResults } = sourcesDomainModel;

    const noResultsMessage = filterService.maybeFilter.ifEmpty(
        () => noSearchResultsMessage,
        () => noLensSourcesAvailableMessage,
    );

    const [modalProps, showModal, lensSourceToDelete] = useModal<ViewSourceModel>();

    if (loading) {
        return <DashboardSpinner />;
    }

    return (
        <ApiErrorNotice error={error}>
            <Visible when={!isAnySourceEnabled}>
                <Container>
                    <NoResults description={noResultsMessage} />
                    <Centered>
                        <AddLensSourceContainer />
                    </Centered>
                </Container>
            </Visible>
            <Visible when={isAnySourceEnabled}>
                <SearchableCard
                    className={searchItemsContainer}
                    headerClassName={searchableCardHeaderStyle}
                    padBody={false}
                    searchPlaceholder="Search by Lens Source Name"
                    onSearch={setSearchValue}
                    searchValue={filterService.maybeFilter.getOrDefault("") ?? ""}
                >
                    <Visible when={!filterService.isValueEmpty && !hasFilteredResults}>
                        <NoResults description={noResultsMessage} />
                    </Visible>
                    {filteredAllSourcesGrouped.map(({ sources, type }) => (
                        <SourcesGroup key={type} sources={sources} type={type} onDelete={showModal} />
                    ))}
                </SearchableCard>
                <Visible when={lensSourceToDelete != null}>
                    <UpdateLensSourceContainer selectedType={lensSourceToDelete?.type ?? LensSourceType.ScheduleV3}>
                        {({ disable, updating }) => (
                            <DeleteLensSourceModal
                                {...modalProps}
                                onDeleteLensSource={disable}
                                lensSource={lensSourceToDelete}
                                deleting={updating}
                            />
                        )}
                    </UpdateLensSourceContainer>
                </Visible>
            </Visible>
        </ApiErrorNotice>
    );
};
